import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { PoApprovalService } from '../../../services/poApproval.service';
import { AuthService } from '../../../services/auth.service';
import { PoApprovalBand, PoApprovalBandSetup } from '../../../interfaces/procurement/poApproval.interface';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import ScrollableTable from '../../common/ScrollableTable';
import SearchableSelect from '../../common/SearchableSelect';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import { formatMoney } from '../../../utils/format';

interface ApproverOption { id: string; label: string }
const ANY: ApproverOption = { id: '', label: 'Any purchase order approver' };

/** Procurement > PO Approval Bands: the PO total picks the number of approvals and the role that must be among them (B4-F). */
export const PoApprovalBands: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution()!!;
  const canApprove = usePermission(PERMISSIONS.PURCHASE_ORDER_APPROVE);
  const canSettings = usePermission(PERMISSIONS.PROCUREMENT_SETTINGS_MANAGE);
  const canEdit = canApprove && canSettings;

  const [setup, setSetup] = useState<PoApprovalBandSetup | null>(null);
  const [bands, setBands] = useState<PoApprovalBand[]>([]);
  const [dirty, setDirty] = useState(false);
  const [edit, setEdit] = useState<{ index: number; band: PoApprovalBand } | null>(null);
  const [removeIndex, setRemoveIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await PoApprovalService.setup(institutionId);
      setSetup(s);
      setBands(s.bands ?? []);
      setDirty(false);
    } catch (e) {
      toast.showApiError(e, 'Failed to load the approval bands');
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const options: ApproverOption[] = [
    ANY,
    ...(setup?.roles ?? []).map(r => ({ id: `role:${r.id}`, label: `Role: ${r.name}` })),
    ...(setup?.permissions ?? []).map(p => ({ id: `perm:${p.key}`, label: `Permission: ${p.label}` })),
  ];
  const optionOf = (b: PoApprovalBand) =>
    b.requiredRoleId ? `role:${b.requiredRoleId}` : b.requiredPermissionKey ? `perm:${b.requiredPermissionKey}` : '';
  const applyOption = (b: PoApprovalBand, id: string): PoApprovalBand => ({
    ...b,
    requiredRoleId: id.startsWith('role:') ? id.slice(5) : null,
    requiredPermissionKey: id.startsWith('perm:') ? id.slice(5) : null,
  });
  // An unsaved band has no server label yet: resolve the chosen role / permission from the setup lists.
  const requirementText = (b: PoApprovalBand) => {
    if (b.requirementLabel) return b.requirementLabel;
    if (b.requiredRoleId) {
      const name = b.requiredRoleName ?? setup?.roles.find(r => r.id === b.requiredRoleId)?.name;
      return `a holder of the role ${name ?? '(unknown role)'}`;
    }
    if (b.requiredPermissionKey) {
      return setup?.permissions.find(p => p.key === b.requiredPermissionKey)?.label ?? b.requiredPermissionKey;
    }
    return 'any purchase order approver';
  };
  // Polish 10: a band that requires a role no one in can approve POs can never be satisfied.
  const roleCannotApprove = (b: PoApprovalBand) => {
    if (!b.requiredRoleId) return null;
    const role = setup?.roles.find(r => r.id === b.requiredRoleId);
    return role && role.canApprovePurchaseOrders === false ? role.name : null;
  };

  const commitEdit = () => {
    if (!edit) return;
    const next = [...bands];
    if (edit.index >= bands.length) next.push(edit.band); else next[edit.index] = edit.band;
    next.sort((a, b) => a.minAmount - b.minAmount);
    setBands(next.map((b, i) => ({ ...b, sequenceNo: i + 1 })));
    setDirty(true);
    setEdit(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const s = await PoApprovalService.replaceBands(institutionId, bands);
      setSetup(s);
      setBands(s.bands ?? []);
      setDirty(false);
      toast.showSuccess('Approval bands saved');
    } catch (e) {
      toast.showApiError(e, 'Failed to save the approval bands');
    } finally {
      setSaving(false);
    }
  };

  // Money is rounded to cents: 0.07 + 0.01 must be 0.08, not 0.08000000000000002 (the server reads that as a gap).
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const lastBand = bands[bands.length - 1];
  const nextMin = bands.length === 0 ? round2((setup?.autoApproveAtOrBelow ?? 0) + 0.01)
    : lastBand.maxAmount == null ? round2(lastBand.minAmount + 0.01) : round2(lastBand.maxAmount + 0.01);

  // Inline checks for the band being edited: min <= max, and no overlap with the other bands.
  const editError = (): string | null => {
    if (!edit) return null;
    const { minAmount, maxAmount } = edit.band;
    if (!(minAmount >= 0)) return 'Enter the amount the band starts from.';
    if (maxAmount != null && maxAmount < minAmount) return 'The upper limit cannot be below the start of the band.';
    const others = bands.filter((_, i) => i !== edit.index);
    const clash = others.find(o => minAmount <= (o.maxAmount ?? Infinity) && (maxAmount ?? Infinity) >= o.minAmount);
    if (clash) {
      return `This overlaps the band ${formatMoney(clash.minAmount)} to ${clash.maxAmount == null ? 'no limit' : formatMoney(clash.maxAmount)}. `
        + 'Set that band\'s limit first or move this range.';
    }
    return null;
  };
  const currentEditError = editError();

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }} data-help-page="procurement.bands.page">
      <Typography variant="h5" sx={{ mb: 2 }}>Purchase Order Approval Bands</Typography>
      <Stack spacing={2}>
        {!canEdit && (
          <Alert severity="info">Read-only: changing bands needs the purchase order approval and procurement settings permissions.</Alert>
        )}
        <Alert severity="info">
          A purchase order at or below {formatMoney(setup?.autoApproveAtOrBelow ?? 0)} is approved automatically (the
          threshold in Procurement Settings). Above it, the total picks the band: how many different people must approve,
          and which role must be among them. The person who created, submitted or amended the PO can never approve it. With no bands, one
          approval by any approver is enough.
        </Alert>
        <Paper sx={{ p: 2 }} data-help-section="procurement.bands.table">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Bands</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button data-help="procurement.bands.add" variant="outlined" disabled={!canEdit}
                onClick={() => setEdit({ index: bands.length, band: { sequenceNo: bands.length + 1, minAmount: nextMin, maxAmount: null, approvalsRequired: 1 } })}>
                Add band
              </Button>
              <Button data-help="procurement.bands.save" variant="contained" disabled={!canEdit || !dirty || saving} onClick={save}>Save bands</Button>
            </Box>
          </Box>
          {bands.length === 0 ? <Alert severity="info">No bands: every PO above the threshold needs one approval.</Alert> : (
            <ScrollableTable>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="procurement.bands.col.no">#</TableCell><TableCell data-help="procurement.bands.col.from" align="right">From</TableCell><TableCell data-help="procurement.bands.col.to" align="right">To</TableCell>
                    <TableCell data-help="procurement.bands.col.approvals" align="right">Approvals</TableCell><TableCell data-help="procurement.bands.col.must">Must include</TableCell>
                    <TableCell data-help="procurement.bands.col.actions" align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bands.map((b, i) => (
                    <TableRow key={`${b.id ?? 'new'}-${i}`}>
                      <TableCell>{b.sequenceNo}</TableCell>
                      <TableCell align="right">{formatMoney(b.minAmount)}</TableCell>
                      <TableCell align="right">{b.maxAmount == null ? 'No limit' : formatMoney(b.maxAmount)}</TableCell>
                      <TableCell align="right">{b.approvalsRequired}</TableCell>
                      <TableCell>{requirementText(b)}</TableCell>
                      <TableCell align="right">
                        <Button data-help="procurement.bands.edit" size="small" disabled={!canEdit} onClick={() => setEdit({ index: i, band: { ...b } })}>Edit</Button>
                        <Button data-help="procurement.bands.remove" size="small" color="error" disabled={!canEdit} onClick={() => setRemoveIndex(i)}>Remove</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollableTable>
          )}
          {dirty && <Alert severity="warning" sx={{ mt: 1 }}>Unsaved changes. Save the bands to apply them to new submissions.</Alert>}
        </Paper>
      </Stack>

      <Dialog data-help-page="procurement.bands.dialog" open={!!edit} onClose={() => setEdit(null)} fullWidth maxWidth="xs">
        <DialogTitle>{edit && edit.index < bands.length ? 'Edit band' : 'Add band'}</DialogTitle>
        <DialogContent>
          {edit && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField data-help="procurement.bands.field.from" label="Total from" type="number" size="small" fullWidth value={edit.band.minAmount}
                onChange={e => setEdit({ ...edit, band: { ...edit.band, minAmount: round2(Number(e.target.value)) } })}
                error={!!currentEditError} />
              <TextField data-help="procurement.bands.field.to" label="Total up to (blank = no limit)" type="number" size="small" fullWidth value={edit.band.maxAmount ?? ''}
                onChange={e => setEdit({ ...edit, band: { ...edit.band, maxAmount: e.target.value === '' ? null : round2(Number(e.target.value)) } })}
                error={!!currentEditError} helperText={currentEditError ?? undefined} />
              <TextField data-help="procurement.bands.field.approvals" select label="Approvals required" size="small" fullWidth value={edit.band.approvalsRequired}
                onChange={e => setEdit({ ...edit, band: { ...edit.band, approvalsRequired: Number(e.target.value) as 1 | 2 } })}>
                <MenuItem value={1}>1 approval</MenuItem>
                <MenuItem value={2}>2 different approvers</MenuItem>
              </TextField>
              <SearchableSelect<ApproverOption>
                options={options}
                value={optionOf(edit.band)}
                onChange={id => setEdit({ ...edit, band: applyOption(edit.band, id) })}
                getOptionLabel={o => o.label}
                data-help="procurement.bands.field.must"
                label="At least one approval from"
                size="small"
                clearable={false}
              />
              {roleCannotApprove(edit.band) && (
                <Alert severity="warning" data-testid="role-cannot-approve">
                  The role {roleCannotApprove(edit.band)} does not hold the permission to approve purchase orders, so
                  no one can satisfy this band. Grant that permission to the role first (Roles), or pick another role.
                </Alert>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button data-help="procurement.bands.dialogCancel" onClick={() => setEdit(null)}>Cancel</Button>
          <Button data-help="procurement.bands.dialogOk" variant="contained" onClick={commitEdit} disabled={!!currentEditError}>OK</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={removeIndex !== null}
        title="Remove this band?"
        message="Bands are applied when you save. Removing a band can leave a gap the server will refuse."
        confirmLabel="Remove"
        confirmColor="error"
        onCancel={() => setRemoveIndex(null)}
        onConfirm={() => {
          setBands(prev => prev.filter((_, i) => i !== removeIndex).map((b, i) => ({ ...b, sequenceNo: i + 1 })));
          setDirty(true);
          setRemoveIndex(null);
        }}
      />
    </Box>
  );
};

export default PoApprovalBands;
